import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import WifiTetheringRoundedIcon from '@mui/icons-material/WifiTetheringRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import LoadingState from '../../components/ui/LoadingState';
import SectionCard from '../../components/ui/SectionCard';
import api from '../../api';
import tokens from '../../theme/tokens';
import { formatCurrency } from '../../utils/image';

const descricao = {
  asaas: 'PIX com QR dinâmico. Taxas lidas direto da sua conta Asaas.',
  mercadopago: 'PIX via Checkout API. Taxa informada manualmente (o Mercado Pago não expõe a taxa por API).',
  simulado: 'Somente no ambiente DEV: gera um PIX fictício e permite simular o pagamento.',
};

// Cálculo igual ao da API (para a simulação ao editar).
function calcularTaxa(valor, t) {
  let bruto = Number(t.fixa) > 0 ? Number(t.fixa) : (Number(valor) * Number(t.percentual || 0)) / 100;
  if (Number(t.minimo) > 0) bruto = Math.max(bruto, Number(t.minimo));
  if (Number(t.maximo) > 0) bruto = Math.min(bruto, Number(t.maximo));
  return Math.round(bruto * 100) / 100;
}
function composicao(valor, t, modo) {
  if (modo === 'repassar') {
    let cobrado = valor + calcularTaxa(valor, t);
    for (let i = 0; i < 5; i++) cobrado = Math.round((valor + calcularTaxa(cobrado, t)) * 100) / 100;
    return { cobrado, taxa: Math.round((cobrado - valor) * 100) / 100, liquido: valor };
  }
  const taxa = calcularTaxa(valor, t);
  return { cobrado: valor, taxa, liquido: Math.round((valor - taxa) * 100) / 100 };
}

const CampoTaxa = ({ label, value, onChange, sufixo, prefixo, helper }) => (
  <TextField
    label={label}
    size="small"
    type="number"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    inputProps={{ min: 0, step: '0.01' }}
    helperText={helper}
    InputProps={{
      startAdornment: prefixo ? <InputAdornment position="start">{prefixo}</InputAdornment> : undefined,
      endAdornment: sufixo ? <InputAdornment position="end">{sufixo}</InputAdornment> : undefined,
    }}
    fullWidth
  />
);

const GatewayCard = ({ gateway, modoTaxa, onSalvar, onAtivar, onTestar, onAtualizarTaxas, ocupado }) => {
  const [cred, setCred] = useState(gateway.credenciais);
  const [taxa, setTaxa] = useState(gateway.taxa);
  const [simulacao, setSimulacao] = useState(40);

  useEffect(() => {
    setCred(gateway.credenciais);
    setTaxa(gateway.taxa);
  }, [gateway]);

  const exemplo = composicao(Number(simulacao) || 0, taxa, modoTaxa);
  const setT = (campo) => (valor) => setTaxa({ ...taxa, [campo]: valor, origem: 'manual' });

  return (
    <Card sx={{ height: '100%', borderColor: gateway.ativo ? tokens.color.brand : undefined, borderWidth: gateway.ativo ? 2 : 1 }}>
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {gateway.id === 'simulado' && <ScienceRoundedIcon color="warning" />}
              <Typography variant="h6">{gateway.nome}</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">{descricao[gateway.id]}</Typography>
          </Box>
          <Stack spacing={0.5} alignItems="flex-end">
            {gateway.ativo && <Chip size="small" color="primary" icon={<CheckCircleRoundedIcon />} label="Em uso" />}
            <Chip size="small" variant="outlined" color={gateway.configurado ? 'success' : 'default'} label={gateway.configurado ? 'Configurado' : 'Sem credenciais'} />
          </Stack>
        </Stack>

        {gateway.id !== 'simulado' && (
          <>
            <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">Credenciais</Typography></Divider>
            <Stack spacing={1.5}>
              {gateway.id === 'asaas' ? (
                <>
                  <TextField select size="small" label="Ambiente" value={cred.ambiente || 'sandbox'} onChange={(e) => setCred({ ...cred, ambiente: e.target.value })}>
                    <MenuItem value="sandbox">Sandbox (testes)</MenuItem>
                    <MenuItem value="producao">Produção</MenuItem>
                  </TextField>
                  <TextField size="small" label="Chave de API" value={cred.apiKey || ''} onChange={(e) => setCred({ ...cred, apiKey: e.target.value })} placeholder="$aact_..." helperText="Asaas › Integrações › Chaves de API" />
                  <TextField
                    size="small"
                    label="Token do webhook"
                    value={cred.webhookToken || ''}
                    onChange={(e) => setCred({ ...cred, webhookToken: e.target.value })}
                    helperText={`Deve ser idêntico ao token do webhook no Asaas. Atual: ${gateway.credenciais.webhookTokenInfo || 'ausente'}${gateway.credenciais.webhookTokenOrigem ? ` (${gateway.credenciais.webhookTokenOrigem})` : ''}`}
                  />
                </>
              ) : (
                <>
                  <TextField size="small" label="Access token" value={cred.accessToken || ''} onChange={(e) => setCred({ ...cred, accessToken: e.target.value })} placeholder="APP_USR-..." helperText="Mercado Pago › Suas integrações › Credenciais" />
                  <TextField size="small" label="Assinatura secreta do webhook" value={cred.webhookSecret || ''} onChange={(e) => setCred({ ...cred, webhookSecret: e.target.value })} />
                </>
              )}
              {cred.origem === 'env' && <Alert severity="info" variant="outlined" sx={{ py: 0 }}>Usando as credenciais do arquivo .env. Salvar aqui passa a usar as do painel.</Alert>}
              {gateway.webhookUrl && (
                <TextField
                  size="small"
                  label="URL do webhook (cadastre no painel do gateway)"
                  value={gateway.webhookUrl}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => navigator.clipboard.writeText(gateway.webhookUrl)}><ContentCopyRoundedIcon fontSize="small" /></IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {gateway.ultimoWebhook ? (
                <Alert severity={gateway.ultimoWebhook.ok ? 'success' : 'error'} variant="outlined" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="body2" fontWeight={700}>
                    Último webhook: {gateway.ultimoWebhook.ok ? 'aceito' : `recusado (${gateway.ultimoWebhook.status})`} · {new Date(gateway.ultimoWebhook.em).toLocaleString('pt-BR')}
                  </Typography>
                  {gateway.ultimoWebhook.evento && <Typography variant="caption" display="block">Evento: {gateway.ultimoWebhook.evento}</Typography>}
                  <Typography variant="caption" display="block">{gateway.ultimoWebhook.motivo}</Typography>
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary">Nenhum webhook recebido ainda.</Typography>
              )}
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">Taxa do PIX</Typography>
        </Divider>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {taxa.origem === 'api' ? 'Atualizada da API' : 'Informada manualmente'}
            {taxa.atualizadaEm ? ` em ${new Date(taxa.atualizadaEm).toLocaleString('pt-BR')}` : ''}
          </Typography>
          {gateway.taxaAutomatica && (
            <Button size="small" startIcon={<SyncRoundedIcon />} onClick={() => onAtualizarTaxas(gateway.id)} disabled={ocupado || !gateway.configurado}>
              Atualizar da API
            </Button>
          )}
        </Stack>
        <Grid container spacing={1.5}>
          <Grid item xs={6}><CampoTaxa label="Percentual" value={taxa.percentual} onChange={setT('percentual')} sufixo="%" /></Grid>
          <Grid item xs={6}><CampoTaxa label="Valor fixo" value={taxa.fixa} onChange={setT('fixa')} prefixo="R$" helper="Se > 0, substitui o %" /></Grid>
          <Grid item xs={6}><CampoTaxa label="Mínimo" value={taxa.minimo} onChange={setT('minimo')} prefixo="R$" /></Grid>
          <Grid item xs={6}><CampoTaxa label="Máximo" value={taxa.maximo} onChange={setT('maximo')} prefixo="R$" /></Grid>
        </Grid>
        {Number(taxa.isencoesMensais) > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            A conta tem {taxa.isencoesMensais} recebimentos PIX sem taxa por mês (o sistema considera a taxa em todos, por segurança).
          </Typography>
        )}

        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>Simulação</Typography>
            <TextField size="small" type="number" value={simulacao} onChange={(e) => setSimulacao(e.target.value)} sx={{ width: 120 }} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
          </Stack>
          <Stack spacing={0.25}>
            <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Jogador paga</Typography><Typography variant="body2" fontWeight={700}>{formatCurrency(exemplo.cobrado)}</Typography></Stack>
            <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Taxa do gateway</Typography><Typography variant="body2" color="error.main">- {formatCurrency(exemplo.taxa)}</Typography></Stack>
            <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">Clube recebe</Typography><Typography variant="body2" fontWeight={700} color="success.main">{formatCurrency(exemplo.liquido)}</Typography></Stack>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={() => onSalvar(gateway.id, cred, taxa)} disabled={ocupado}>Salvar</Button>
          <Button variant="outlined" startIcon={<WifiTetheringRoundedIcon />} onClick={() => onTestar(gateway.id)} disabled={ocupado || !gateway.configurado}>Testar conexão</Button>
          {!gateway.ativo && (
            <Button color="secondary" variant="outlined" onClick={() => onAtivar(gateway.id)} disabled={ocupado || !gateway.configurado}>Usar este gateway</Button>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

// Administração › Gateways: formas de pagamento, credenciais, taxas e quem paga a taxa.
const GatewaysAdmin = ({ onMessage }) => {
  const [dados, setDados] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const executar = async (fn, sucesso) => {
    setOcupado(true);
    try {
      const response = await fn();
      if (response && response.data && response.data.gateways) setDados(response.data);
      if (sucesso) onMessage && onMessage(typeof sucesso === 'function' ? sucesso(response.data) : sucesso);
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || 'Não foi possível concluir a ação.', 'error');
    }
    setOcupado(false);
  };

  useEffect(() => {
    executar(() => api.get('admin/gateways'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!dados) return <LoadingState label="Carregando gateways..." />;

  const salvar = (id, cred, taxa) => {
    const corpo = { taxas: { [id]: taxa } };
    if (id === 'asaas') corpo.asaas = { apiKey: cred.apiKey, webhookToken: cred.webhookToken, ambiente: cred.ambiente };
    if (id === 'mercadopago') corpo.mercadopago = { accessToken: cred.accessToken, webhookSecret: cred.webhookSecret };
    executar(() => api.put('admin/gateways', corpo), 'Configurações salvas');
  };

  return (
    <Stack spacing={3}>
      {dados.ambienteDev && (
        <Alert severity="warning" icon={<ScienceRoundedIcon />}>Ambiente de desenvolvimento: o gateway <strong>Simulado</strong> está disponível para testes sem cobrança real.</Alert>
      )}

      <SectionCard title="Quem paga a taxa do PIX?" subtitle="Vale para todas as novas cobranças geradas (mensalidades e diárias)">
        <ToggleButtonGroup
          exclusive
          color="primary"
          value={dados.modoTaxa}
          onChange={(e, modo) => modo && executar(() => api.put('admin/gateways', { modoTaxa: modo }), modo === 'assumir' ? 'O clube assume a taxa' : 'A taxa será repassada ao jogador')}
          sx={{ flexWrap: 'wrap' }}
        >
          <ToggleButton value="assumir" sx={{ px: 3, py: 1.5, alignItems: 'flex-start', textAlign: 'left' }}>
            <Stack direction="row" spacing={1.5}>
              <AccountBalanceRoundedIcon />
              <Box>
                <Typography fontWeight={700}>Clube assume</Typography>
                <Typography variant="caption" sx={{ textTransform: 'none' }} display="block">Jogador paga o valor exato; a taxa sai do caixa.</Typography>
              </Box>
            </Stack>
          </ToggleButton>
          <ToggleButton value="repassar" sx={{ px: 3, py: 1.5, alignItems: 'flex-start', textAlign: 'left' }}>
            <Stack direction="row" spacing={1.5}>
              <PersonRoundedIcon />
              <Box>
                <Typography fontWeight={700}>Repassar ao jogador</Typography>
                <Typography variant="caption" sx={{ textTransform: 'none' }} display="block">Jogador paga valor + taxa; o clube recebe o valor integral.</Typography>
              </Box>
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Em ambos os casos a dívida do jogador no extrato é quitada pelo valor da cobrança. A taxa é lançada como débito no caixa do clube.
        </Typography>
      </SectionCard>

      {ocupado && <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}

      <Grid container spacing={3}>
        {dados.gateways.map((gateway) => (
          <Grid item xs={12} md={6} lg={dados.gateways.length === 3 ? 4 : 6} key={gateway.id}>
            <GatewayCard
              gateway={gateway}
              modoTaxa={dados.modoTaxa}
              ocupado={ocupado}
              onSalvar={salvar}
              onAtivar={(id) => executar(() => api.put('admin/gateways', { provider: id }), `${gateway.nome} passou a ser o gateway em uso`)}
              onTestar={(id) => executar(() => api.post(`admin/gateways/${id}/testar`), (r) => `Conexão ok: ${r.conta}`)}
              onAtualizarTaxas={(id) => executar(() => api.post(`admin/gateways/${id}/taxas`), 'Taxas atualizadas da API')}
            />
          </Grid>
        ))}
      </Grid>
      {dados.updatedAt && (
        <Typography variant="caption" color="text.secondary">Última alteração por {dados.atualizadoPor} em {new Date(dados.updatedAt).toLocaleString('pt-BR')}</Typography>
      )}
    </Stack>
  );
};

export default GatewaysAdmin;
